import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import RiskMetricsCard from './components/RiskMetricsCard';
import ExposureChart from './components/ExposureChart';
import RiskAlertsFeed from './components/RiskAlertsFeed';
import CorrelationMatrix from './components/CorrelationMatrix';
import GlobalRiskControls from './components/GlobalRiskControls';

const RiskManagementCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Initialize language from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Mock risk metrics data with real-time updates
  const [riskMetrics, setRiskMetrics] = useState({
    marginLevel: { value: 156.8, threshold: '100%', trend: -2.3, alertLevel: 'normal' },
    totalExposure: { value: 387500, threshold: '$500K', trend: 5.7, alertLevel: 'warning' },
    correlationRisk: { value: 0.73, threshold: '0.80', trend: 1.2, alertLevel: 'warning' },
    varCalculation: { value: 23450, threshold: '$25K', trend: -0.8, alertLevel: 'critical' }
  });

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setRiskMetrics(prev => ({
        marginLevel: {
          ...prev?.marginLevel,
          value: prev?.marginLevel?.value + (Math.random() - 0.5) * 2,
          trend: (Math.random() - 0.5) * 5
        },
        totalExposure: {
          ...prev?.totalExposure,
          value: prev?.totalExposure?.value + (Math.random() - 0.5) * 5000,
          trend: (Math.random() - 0.5) * 10
        },
        correlationRisk: {
          ...prev?.correlationRisk,
          value: Math.max(0, Math.min(1, prev?.correlationRisk?.value + (Math.random() - 0.5) * 0.05)),
          trend: (Math.random() - 0.5) * 3
        },
        varCalculation: {
          ...prev?.varCalculation,
          value: prev?.varCalculation?.value + (Math.random() - 0.5) * 1000,
          trend: (Math.random() - 0.5) * 4
        }
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Risk Management Center - Zonex Trader</title>
        <meta name="description" content="Comprehensive risk monitoring and exposure management dashboard for trading positions" />
      </Helmet>
      {/* Sidebar */}
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        {/* Header */}
        <Header />

        {/* Page Content */}
        <main className="p-6 pb-20 lg:pb-6">
          {/* Global Risk Controls - Full Width */}
          <div className="mb-6">
            <GlobalRiskControls />
          </div>

          {/* Risk Metrics Cards - 4 Equal Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
            <RiskMetricsCard
              title="Margin Level"
              value={riskMetrics?.marginLevel?.value?.toFixed(1)}
              unit="%"
              threshold={riskMetrics?.marginLevel?.threshold}
              icon="DollarSign"
              trend={riskMetrics?.marginLevel?.trend}
              alertLevel={riskMetrics?.marginLevel?.value < 120 ? 'warning' : 'normal'}
            />
            <RiskMetricsCard
              title="Total Exposure"
              value={formatCurrency(riskMetrics?.totalExposure?.value)}
              threshold={riskMetrics?.totalExposure?.threshold}
              icon="Target"
              trend={riskMetrics?.totalExposure?.trend}
              alertLevel={riskMetrics?.totalExposure?.value > 450000 ? 'warning' : 'normal'}
            />
            <RiskMetricsCard
              title="Correlation Risk"
              value={riskMetrics?.correlationRisk?.value?.toFixed(2)}
              threshold={riskMetrics?.correlationRisk?.threshold}
              icon="GitBranch"
              trend={riskMetrics?.correlationRisk?.trend}
              alertLevel={riskMetrics?.correlationRisk?.value > 0.7 ? 'warning' : 'normal'}
            />
            <RiskMetricsCard
              title="VaR (95%)"
              value={formatCurrency(riskMetrics?.varCalculation?.value)}
              threshold={riskMetrics?.varCalculation?.threshold}
              icon="TrendingDown"
              trend={riskMetrics?.varCalculation?.trend}
              alertLevel={riskMetrics?.varCalculation?.value > 24000 ? 'critical' : 'normal'}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-16 gap-6 mb-6">
            {/* Exposure Chart - 10 columns on desktop */}
            <div className="xl:col-span-10">
              <ExposureChart />
            </div>

            {/* Risk Alerts Feed - 6 columns on desktop */}
            <div className="xl:col-span-6">
              <RiskAlertsFeed />
            </div>
          </div>

          {/* Correlation Matrix - Full Width */}
          <div className="mb-6">
            <CorrelationMatrix />
          </div>

          {/* Additional Risk Information */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Risk Summary */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Risk Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Overall Risk Score</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-2 bg-muted rounded-full">
                      <div className="w-3/4 h-2 bg-amber-500 rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium text-amber-600 dark:text-amber-400">High</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Portfolio Diversification</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Good</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Leverage Utilization</span>
                  <span className="text-sm font-medium text-amber-600 dark:text-amber-400">78%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Risk-Adjusted Return</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">1.34</span>
                </div>
              </div>
            </div>

            {/* Market Conditions */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Market Conditions</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Market Volatility</span>
                  <span className="text-sm font-medium text-red-600 dark:text-red-400">High</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Liquidity Conditions</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Normal</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Economic Events</span>
                  <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Medium Impact</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">News Sentiment</span>
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Neutral</span>
                </div>
              </div>
            </div>

            {/* Compliance Status */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Compliance Status</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Regulatory Limits</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Compliant</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Position Limits</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Within Limits</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Reporting Status</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Up to Date</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Last Audit</span>
                  <span className="text-sm font-medium text-muted-foreground">2 days ago</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RiskManagementCenter;